import React, { Component } from 'react';
import { Heart, MessageCircle, Share2, Eye, ThumbsDown, Maximize2, MoreVertical, Download, FileText, Tag } from 'lucide-react';
import apiService from '@/services/api';
import styles from './PostCard.module.scss';

interface PostAttachment {
  id: string;
  attachmentType: 'image' | 'video' | 'gif' | 'file' | 'pdf' | 'word';
  fileUrl?: string;
  thumbnailUrl?: string;
  fileName?: string;
  fileSize?: number;
}

interface PostAuthor {
  id: string;
  displayName: string;
  profilePic?: string;
}

interface Post {
  id: string;
  title?: string;
  description?: string;
  contentText?: string;
  postType: string;
  attachments?: PostAttachment[];
  author: PostAuthor;
  likesCount: number;
  dislikesCount: number;
  commentsCount: number;
  sharesCount: number;
  viewsCount: number;
  isLiked: boolean;
  isDisliked: boolean;
  createdAt: string;
  publishedAt?: string;
  status?: 'draft' | 'published';
  tags?: string[];
}

interface PostCardProps {
  post: Post;
  onLike?: (postId: string) => void;
  onDislike?: (postId: string) => void;
  onComment?: (postId: string) => void;
  onShare?: (postId: string) => void;
  onFullscreen?: (post: Post) => void;
  onView?: (postId: string) => void;
  showActions?: boolean;
}

interface PostCardState {
  isLiked: boolean;
  isDisliked: boolean;
  likesCount: number;
  dislikesCount: number;
  commentsCount: number;
  sharesCount: number;
  viewsCount: number;
  isProcessing: boolean;
  showFullContent: boolean;
}

export class PostCard extends Component<PostCardProps, PostCardState> {
  private viewStartTime: number = 0;
  private hasTrackedView: boolean = false; // Prevent duplicate view tracking
  private isTrackingView: boolean = false; // Prevent concurrent tracking calls
  private static trackedPosts: Set<string> = new Set(); // Track viewed posts across all instances
  private static trackingInProgress: Set<string> = new Set(); // Track posts currently being tracked

  constructor(props: PostCardProps) {
    super(props);
    this.state = {
      isLiked: props.post.isLiked || false,
      isDisliked: props.post.isDisliked || false,
      likesCount: props.post.likesCount || 0,
      dislikesCount: props.post.dislikesCount || 0,
      commentsCount: props.post.commentsCount || 0,
      sharesCount: props.post.sharesCount || 0,
      viewsCount: props.post.viewsCount || 0,
      isProcessing: false,
      showFullContent: false,
    };
  }

  componentDidMount() {
    // Track view only once per post, even if component remounts
    const postId = this.props.post.id;
    if (!PostCard.trackedPosts.has(postId) && !this.hasTrackedView && !PostCard.trackingInProgress.has(postId)) {
      PostCard.trackingInProgress.add(postId);
      this.trackView();
      PostCard.trackedPosts.add(postId);
      this.hasTrackedView = true;
      this.viewStartTime = Date.now();
    }
  }

  componentWillUnmount() {
    // Track final view duration only if we tracked the initial view
    if (this.hasTrackedView) {
      const duration = Math.floor((Date.now() - this.viewStartTime) / 1000);
      if (duration > 0) {
        this.trackView(duration);
      }
    }
  }

  trackView = async (duration?: number) => {
    const postId = this.props.post.id;
    
    // Prevent duplicate concurrent calls
    if (this.isTrackingView && !duration) {
      return;
    }

    // For initial view tracking, check if already in progress
    if (!duration && PostCard.trackingInProgress.has(postId)) {
      return;
    }

    this.isTrackingView = true;
    if (!duration) {
      PostCard.trackingInProgress.add(postId);
    }

    try {
      // Track view silently without triggering feed reload
      await apiService.trackPostView(postId, {
        viewDurationSeconds: duration,
        interactionType: 'view',
      });
      // Don't call onView callback to avoid reloading the feed
      // The view tracking is independent and shouldn't trigger UI updates
    } catch (error) {
      console.error('Failed to track view:', error);
    } finally {
      this.isTrackingView = false;
      if (!duration) {
        PostCard.trackingInProgress.delete(postId);
      }
    }
  };

  handleLike = async () => {
    if (this.state.isProcessing) return;

    this.setState({ isProcessing: true });
    const wasLiked = this.state.isLiked;
    const wasDisliked = this.state.isDisliked;

    // Optimistic update
    this.setState({
      isLiked: !wasLiked,
      isDisliked: false,
      likesCount: wasLiked ? this.state.likesCount - 1 : this.state.likesCount + 1,
      dislikesCount: wasDisliked ? this.state.dislikesCount - 1 : this.state.dislikesCount,
    });

    try {
      if (wasLiked) {
        await apiService.unlikePost(this.props.post.id);
      } else {
        await apiService.likePost(this.props.post.id);
        if (wasDisliked) {
          await apiService.undislikePost(this.props.post.id);
        }
      }
      if (this.props.onLike) {
        this.props.onLike(this.props.post.id);
      }
    } catch (error) {
      // Revert on error
      this.setState({
        isLiked: wasLiked,
        isDisliked: wasDisliked,
        likesCount: this.props.post.likesCount || 0,
        dislikesCount: this.props.post.dislikesCount || 0,
      });
      console.error('Failed to like post:', error);
    } finally {
      this.setState({ isProcessing: false });
    }
  };

  handleDislike = async () => {
    if (this.state.isProcessing) return;

    this.setState({ isProcessing: true });
    const wasDisliked = this.state.isDisliked;
    const wasLiked = this.state.isLiked;

    // Optimistic update
    this.setState({
      isDisliked: !wasDisliked,
      isLiked: false,
      dislikesCount: wasDisliked ? this.state.dislikesCount - 1 : this.state.dislikesCount + 1,
      likesCount: wasLiked ? this.state.likesCount - 1 : this.state.likesCount,
    });

    try {
      if (wasDisliked) {
        await apiService.undislikePost(this.props.post.id);
      } else {
        await apiService.dislikePost(this.props.post.id);
        if (wasLiked) {
          await apiService.unlikePost(this.props.post.id);
        }
      }
      if (this.props.onDislike) {
        this.props.onDislike(this.props.post.id);
      }
    } catch (error) {
      // Revert on error
      this.setState({
        isLiked: wasLiked,
        isDisliked: wasDisliked,
        likesCount: this.props.post.likesCount || 0,
        dislikesCount: this.props.post.dislikesCount || 0,
      });
      console.error('Failed to dislike post:', error);
    } finally {
      this.setState({ isProcessing: false });
    }
  };

  handleComment = () => {
    if (this.props.onComment) {
      this.props.onComment(this.props.post.id);
    }
  };

  handleShare = async () => {
    try {
      // Track share in backend
      await apiService.sharePost(this.props.post.id);
      this.setState({ sharesCount: this.state.sharesCount + 1 });
      if (this.props.onShare) {
        this.props.onShare(this.props.post.id);
      }
      
      // Generate shareable URL with post ID
      const shareUrl = `${window.location.origin}/customer/social/posts/${this.props.post.id}`;
      const shareTitle = this.props.post.title || 'Check out this post';
      const shareText = this.props.post.description || this.props.post.contentText || '';
      
      // Try to use Web Share API if available
      if (navigator.share) {
        try {
          const shareData = {
            title: shareTitle,
            text: shareText,
            url: shareUrl,
          };
          await navigator.share(shareData);
          return; // Successfully shared via native share
        } catch (shareError: any) {
          // User cancelled or error occurred, fall through to copy to clipboard
          if (shareError.name !== 'AbortError') {
            console.error('Share error:', shareError);
          }
        }
      }
      
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Post link copied to clipboard!');
      } catch (clipboardError) {
        // Fallback: Show URL in prompt
        prompt('Copy this link to share:', shareUrl);
      }
    } catch (error) {
      console.error('Failed to share post:', error);
      alert('Failed to share post. Please try again.');
    }
  };

  handleFullscreen = () => {
    if (this.props.onFullscreen) {
      this.props.onFullscreen(this.props.post);
    }
  };

  formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) {
      return date.toLocaleDateString();
    } else if (days > 0) {
      return `${days}d ago`;
    } else if (hours > 0) {
      return `${hours}h ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return 'Just now';
    }
  };

  renderAttachment = (attachment: PostAttachment) => {
    switch (attachment.attachmentType) {
      case 'image':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <img
              src={attachment.fileUrl || attachment.thumbnailUrl}
              alt={attachment.fileName || 'Post image'}
              className={styles.attachmentImage}
              loading="lazy"
            />
          </div>
        );
      
      case 'video':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <div className={styles.videoContainer}>
              <video
                src={attachment.fileUrl}
                controls
                className={styles.attachmentVideo}
                poster={attachment.thumbnailUrl}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        );
      
      case 'gif':
        return (
          <div key={attachment.id} className={styles.attachment}>
            <img
              src={attachment.fileUrl || attachment.thumbnailUrl}
              alt={attachment.fileName || 'GIF'}
              className={styles.attachmentImage}
              loading="lazy"
            />
          </div>
        );
      
      case 'file':
      case 'pdf':
      case 'word':
        return (
          <div key={attachment.id} className={styles.fileAttachment}>
            <div className={styles.fileIcon}>
              <FileText size={24} />
            </div>
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>{attachment.fileName || 'File'}</span>
              {attachment.fileSize && (
                <span className={styles.fileSize}>{this.formatFileSize(attachment.fileSize)}</span>
              )}
            </div>
            {attachment.fileUrl && (
              <a
                href={attachment.fileUrl}
                download
                className={styles.downloadButton}
                title="Download file"
              >
                <Download size={18} />
              </a>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  render() {
    const { post, showActions = true } = this.props;
    const { isLiked, isDisliked, likesCount, dislikesCount, commentsCount, sharesCount, viewsCount, showFullContent } = this.state;
    const hasContent = post.contentText && post.contentText.length > 200;
    const displayContent = hasContent && !showFullContent
      ? post.contentText!.substring(0, 200) + '...'
      : post.contentText;

    return (
      <div className={styles.postCard}>
        {/* Header */}
        <div className={styles.postHeader}>
          <div className={styles.authorInfo}>
            <img
              src={post.author.profilePic || '/default-avatar.png'}
              alt={post.author.displayName}
              className={styles.authorAvatar}
            />
            <div className={styles.authorDetails}>
              <span className={styles.authorName}>{post.author.displayName}</span>
              <span className={styles.postDate}>
                {post.publishedAt ? this.formatDate(post.publishedAt) : this.formatDate(post.createdAt)}
              </span>
            </div>
          </div>
          <button className={styles.moreButton} title="More options">
            <MoreVertical size={20} />
          </button>
        </div>

        {/* Title */}
        {post.title && (
          <h3 className={styles.postTitle}>{post.title}</h3>
        )}

        {/* Description */}
        {post.description && (
          <p className={styles.postDescription}>{post.description}</p>
        )}

        {/* Content Text */}
        {post.contentText && (
          <div className={styles.postContent}>
            <p>{displayContent}</p>
            {hasContent && (
              <button
                className={styles.readMoreButton}
                onClick={() => this.setState({ showFullContent: !showFullContent })}
              >
                {showFullContent ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        )}

        {/* Attachments */}
        {post.attachments && post.attachments.length > 0 && (
          <div className={styles.attachmentsContainer}>
            {post.attachments.map(attachment => this.renderAttachment(attachment))}
          </div>
        )}

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className={styles.postTags}>
            <Tag size={16} className={styles.tagsIcon} />
            <div className={styles.tagsList}>
              {post.tags.map((tag, index) => (
                <span key={index} className={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className={styles.postStats}>
          <span className={styles.statItem}>
            <Eye size={16} />
            {viewsCount.toLocaleString()} views
          </span>
          <span className={styles.statItem}>
            <Heart size={16} />
            {likesCount.toLocaleString()} likes
          </span>
          {dislikesCount > 0 && (
            <span className={styles.statItem}>
              <ThumbsDown size={16} />
              {dislikesCount.toLocaleString()} dislikes
            </span>
          )}
          <span className={styles.statItem}>
            <MessageCircle size={16} />
            {commentsCount.toLocaleString()} comments
          </span>
          {sharesCount > 0 && (
            <span className={styles.statItem}>
              <Share2 size={16} />
              {sharesCount.toLocaleString()} shares
            </span>
          )}
        </div>

        {/* Actions */}
        {showActions && (
          <div className={styles.postActions}>
            <button
              className={`${styles.actionButton} ${isLiked ? styles.active : ''}`}
              onClick={this.handleLike}
              disabled={this.state.isProcessing}
              title="Like"
            >
              <Heart size={20} fill={isLiked ? 'currentColor' : 'none'} />
              <span>Like</span>
            </button>
            <button
              className={`${styles.actionButton} ${isDisliked ? styles.active : ''}`}
              onClick={this.handleDislike}
              disabled={this.state.isProcessing}
              title="Dislike"
            >
              <ThumbsDown size={20} fill={isDisliked ? 'currentColor' : 'none'} />
              <span>Dislike</span>
            </button>
            <button
              className={styles.actionButton}
              onClick={this.handleComment}
              title="Comments"
            >
              <MessageCircle size={20} />
              <span>Comments</span>
            </button>
            <button
              className={styles.actionButton}
              onClick={this.handleShare}
              title="Share"
            >
              <Share2 size={20} />
              <span>Share</span>
            </button>
            <button
              className={styles.actionButton}
              onClick={this.handleFullscreen}
              title="View fullscreen"
            >
              <Maximize2 size={20} />
              <span>Fullscreen</span>
            </button>
          </div>
        )}

        {/* Status Badge */}
        {post.status === 'draft' && (
          <div className={styles.draftBadge}>Draft</div>
        )}
      </div>
    );
  }
}

export default PostCard;

